import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const ROLE_COLORS = {
  admin: '#ef4444', director: '#7c3aed', sales_head: '#2563eb',
  sales: '#16a34a', sales_coordinator: '#0891b2', sourcing: '#d97706', receptionist: '#64748b'
};

const ACTION_LABELS = {
  followup_created: { icon: '📞', label: 'Created follow-up', color: '#3b82f6' },
  followup_completed: { icon: '✅', label: 'Completed follow-up', color: '#16a34a' },
  sale_created: { icon: '💰', label: 'Recorded sale', color: '#d97706' },
  phone_reveal: { icon: '📱', label: 'Revealed phone', color: '#8b5cf6' },
  client_view: { icon: '👁️', label: 'Viewed client', color: '#64748b' }
};

const formatCurrency = (n) => {
  if (!n) return '0';
  if (n >= 10000000) return (n / 10000000).toFixed(2) + ' Cr';
  if (n >= 100000) return (n / 100000).toFixed(1) + ' L';
  return n.toLocaleString('en-IN');
};

const ActivityReport = ({ apiBase }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [period, setPeriod] = useState('day');
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => { fetchActivity(); }, [selectedDate, period]);

  const fetchActivity = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/reports/team-activity?date=${selectedDate}&period=${period}`);
      setData(res.data);
    } catch (error) {
      console.error('Error fetching activity:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) return <div className="loading">Loading activity report...</div>;

  const team = data?.team_summary || [];
  const feed = data?.activity_feed || [];
  const totals = data?.totals || {};

  const chartData = team.filter(u => u.total_score > 0).map(u => ({
    name: u.user_name?.split(' ')[0] || 'Unknown',
    'Follow-ups': u.followups_created + u.followups_completed,
    'Sales': u.sales_created * 10,
    'Score': u.total_score
  }));

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: '24px' }}>📋 Team Activity Report</h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Track daily performance and engagement of every team member</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
            {['day', 'week', 'month'].map(p => (
              <button key={p} onClick={() => setPeriod(p)}
                style={{
                  padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                  background: period === p ? '#3b82f6' : '#fff', color: period === p ? '#fff' : '#64748b'
                }}>{p === 'day' ? 'Today' : p === 'week' ? '7 Days' : '30 Days'}</button>
            ))}
          </div>
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px' }} />
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Follow-ups Created', value: totals.followups_created, icon: '📝', color: '#3b82f6', bg: '#eff6ff' },
          { label: 'Follow-ups Done', value: totals.followups_completed, icon: '✅', color: '#16a34a', bg: '#f0fdf4' },
          { label: 'Walk-ins', value: totals.walkins, icon: '🚶', color: '#7c3aed', bg: '#f5f3ff' },
          { label: 'Sales Closed', value: totals.sales, icon: '💰', color: '#d97706', bg: '#fffbeb' },
          { label: 'Revenue', value: `₹${formatCurrency(totals.revenue)}`, icon: '📊', color: '#059669', bg: '#ecfdf5' },
          { label: 'Active Team', value: team.filter(u => u.total_score > 0).length + '/' + team.length, icon: '👥', color: '#64748b', bg: '#f8fafc' },
        ].map((s, i) => (
          <div key={i} style={{ background: s.bg, border: `1px solid ${s.color}20`, borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>{s.icon}</div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: s.color, opacity: 0.8 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Performance Chart */}
      {chartData.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h2 className="card-title">Activity Score by Team Member</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" fontSize={12} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Bar dataKey="Score" radius={[6, 6, 0, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill={['#3b82f6', '#16a34a', '#d97706', '#7c3aed', '#ef4444', '#0891b2', '#64748b'][i % 7]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '20px' }}>
        {/* Team Performance Table */}
        <div className="card">
          <h2 className="card-title">Individual Performance</h2>
          <div className="table-container" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Team Member</th>
                  <th>Role</th>
                  <th style={{ textAlign: 'center' }}>📝 FU Created</th>
                  <th style={{ textAlign: 'center' }}>✅ FU Done</th>
                  <th style={{ textAlign: 'center' }}>💰 Sales</th>
                  <th style={{ textAlign: 'center' }}>📱 Phones</th>
                  <th style={{ textAlign: 'center' }}>👁️ Views</th>
                  <th style={{ textAlign: 'center' }}>⭐ Score</th>
                </tr>
              </thead>
              <tbody>
                {team.map((u, idx) => (
                  <tr key={u.user_id} onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                    style={{ cursor: 'pointer', background: expandedUser === u.user_id ? '#f0f9ff' : 'transparent' }}>
                    <td>
                      <span style={{
                        display: 'inline-flex', width: '24px', height: '24px', borderRadius: '50%', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', fontWeight: 700,
                        background: idx === 0 ? '#fef3c7' : idx === 1 ? '#f1f5f9' : idx === 2 ? '#fed7aa' : '#f8fafc',
                        color: idx === 0 ? '#d97706' : idx === 1 ? '#64748b' : idx === 2 ? '#ea580c' : '#94a3b8'
                      }}>{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}</span>
                    </td>
                    <td><strong>{u.user_name}</strong></td>
                    <td>
                      <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600, background: (ROLE_COLORS[u.role] || '#64748b') + '15', color: ROLE_COLORS[u.role] || '#64748b' }}>
                        {(u.role || '').replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{u.followups_created}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: u.followups_completed > 0 ? '#16a34a' : '#d1d5db' }}>{u.followups_completed}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: u.sales_created > 0 ? '#d97706' : '#d1d5db' }}>{u.sales_created}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: u.phone_reveals > 0 ? '#8b5cf6' : '#d1d5db' }}>{u.phone_reveals}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: u.client_views > 0 ? '#64748b' : '#d1d5db' }}>{u.client_views}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 700,
                        background: u.total_score > 50 ? '#dcfce7' : u.total_score > 10 ? '#fef3c7' : u.total_score > 0 ? '#fef2f2' : '#f8fafc',
                        color: u.total_score > 50 ? '#16a34a' : u.total_score > 10 ? '#d97706' : u.total_score > 0 ? '#ef4444' : '#d1d5db'
                      }}>{u.total_score}</span>
                    </td>
                  </tr>
                ))}
                {team.length === 0 && (
                  <tr><td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No team activity data found for this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '12px 16px', fontSize: '11px', color: '#94a3b8', background: '#f8fafc', borderRadius: '0 0 8px 8px' }}>
            Score = Follow-ups Created ×5 + Completed ×10 + Sales ×50 + Phone Reveals ×2 + Client Views ×1
          </div>
        </div>

        {/* Activity Feed */}
        <div className="card" style={{ maxHeight: '600px', display: 'flex', flexDirection: 'column' }}>
          <h2 className="card-title">📡 Live Activity Feed</h2>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {feed.length > 0 ? feed.map((item, idx) => {
              const actionInfo = ACTION_LABELS[item.action] || { icon: '📌', label: item.action, color: '#64748b' };
              return (
                <div key={idx} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: actionInfo.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>
                    {actionInfo.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#1e293b' }}>{item.user_name || 'System'}</strong>
                      <span style={{ color: '#64748b' }}> {actionInfo.label} for </span>
                      <strong style={{ color: '#3b82f6' }}>{item.target_name}</strong>
                    </div>
                    {item.detail && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.detail}</div>}
                    <div style={{ fontSize: '10px', color: '#cbd5e1', marginTop: '3px' }}>
                      {item.created_at ? new Date(item.created_at).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }) : '-'}
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8' }}>
                No activity recorded for this period.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityReport;
